/** Based on Mass PFML: https://github.com/navapbc/archive-massgov-pfml/blob/main/portal/src/components/ButtonLink.tsx */

import classnames from 'classnames';

/**
 * Next.js [`Link`](https://nextjs.org/docs/api-reference/next/link)
 * styled as a button. Provides client-side transitions between routes.
 */
import Link from 'next/link';
import React from 'react';

/*
This component wraps Next.js Link component and provides functionality similar to the trussworks button component.
The idea is to extend the next link to fit our needs (external variant, accessibility, button styling, etc) while keeping the SPA navigation functionality.
*/

type CustomLinkProps = {
  children: React.ReactNode;
  'aria-label'?: React.AriaAttributes['aria-label'];
  'aria-controls'?: React.AriaAttributes['aria-controls'];
  'aria-expanded'?: React.AriaAttributes['aria-expanded'];
  'data-testid'?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  href: string;
  variation?: 'outline' | 'secondary' | 'accent-cool' | 'unstyled';
  inversed?: boolean;
  external?: boolean;
  button?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const CustomLink = (props: CustomLinkProps) => {
  const linkType = props.button ? 'button' : 'link';
  const classes = classnames(
    props.className,
    `usa-${linkType}`,
    props.external ? 'usa-link--external' : '',
    props.variation ? `usa-${linkType}--${props.variation}` : '',
    {
      [`usa-${linkType}--inverse`]: props.inversed,
      // This is weird, but we need this so that the inversed styling
      // kicks in when the variation is unstyled
      [`usa-${linkType}--outline`]: props.inversed && props.variation === 'unstyled',
    },
    { disabled: props.disabled }
  );

  if (props.button) {
    return <ButtonLink {...props} className={classes} />;
  } else {
    return (
      <Link
        className={classes}
        style={props.style}
        href={props.href}
        aria-label={props['aria-label']}
        aria-controls={props['aria-controls']}
        aria-expanded={props['aria-expanded']}
        data-testid={props['data-testid']}
        onClick={props.onClick}
        title={props.title}
        target={props.external ? '_blank' : undefined}
        rel={props.external ? 'noopener' : undefined}
      >
        {props.children}
      </Link>
    );
  }
};

/* Button Link is now internal. To use it, pass button prop to CustomLink */

type ButtonLinkProps = {
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
  'aria-label'?: string;
  'aria-controls'?: React.AriaAttributes['aria-controls'];
  'aria-expanded'?: React.AriaAttributes['aria-expanded'];
  'data-testid'?: string;
  className?: string;
  style?: React.CSSProperties;
  href: string;
  variation?: 'outline' | 'secondary' | 'accent-cool' | 'unstyled';
  inversed?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  external?: boolean;
};

const ButtonLink = (props: ButtonLinkProps) => {
  if (props.disabled) {
    return (
      <button
        type="button"
        className={props.className}
        style={props.style}
        aria-label={props['aria-label']}
        aria-controls={props['aria-controls']}
        aria-expanded={props['aria-expanded']}
        data-testid={props['data-testid']}
        disabled
      >
        {props.children}
      </button>
    );
  }

  return (
    <Link
      role="button"
      href={props.href}
      className={props.className}
      style={props.style}
      data-testid={props['data-testid']}
      aria-label={props['aria-label']}
      aria-controls={props['aria-controls']}
      aria-expanded={props['aria-expanded']}
      title={props.title}
      onClick={props.onClick}
      target={props.external ? '_blank' : undefined}
      rel={props.external ? 'noopener' : undefined}
    >
      {props.children}
    </Link>
  );
};

export default CustomLink;
